import {
  Timestamp,
  collection,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  updateDoc,
  type DocumentData,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db } from './firebase';
import { functions } from './firebase';

export type ContactRequestCategory =
  | 'general'
  | 'technical'
  | 'billing'
  | 'features'
  | 'account';

export type ContactRequestPriority = 'low' | 'medium' | 'high' | 'urgent';

export interface SubmitContactRequestInput {
  name: string;
  email: string;
  phone?: string;
  subject: string;
  message: string;
  category: ContactRequestCategory;
  priority: ContactRequestPriority;
}

export interface SubmitContactRequestResult {
  success: boolean;
  ticketId: string;
}

export interface SubscribeToNewsletterInput {
  email: string;
  source?: string;
}

export interface SubscribeToNewsletterResult {
  success: boolean;
}

export type SupportTicketStatus =
  | 'new'
  | 'in_progress'
  | 'replied'
  | 'closed';

export interface SupportTicket {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  subject: string;
  message: string;
  category: ContactRequestCategory;
  priority: ContactRequestPriority;
  status: SupportTicketStatus;
  source: string;
  requesterUid: string | null;
  internalNote: string;
  createdAt: Date | null;
  updatedAt: Date | null;
}

export interface UpdateSupportTicketInput {
  ticketId: string;
  status: SupportTicketStatus;
  internalNote: string;
}

const SUPPORT_TICKETS_COLLECTION = 'supportTickets';

function toDate(value: unknown): Date | null {
  if (value && typeof (value as { toDate?: unknown }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate();
  }

  if (typeof value === 'string' || typeof value === 'number') {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  return null;
}

function normalizeTicket(id: string, data: DocumentData): SupportTicket {
  return {
    id,
    name: typeof data.name === 'string' ? data.name : 'غير محدد',
    email: typeof data.email === 'string' ? data.email : '',
    phone: typeof data.phone === 'string' ? data.phone : null,
    subject: typeof data.subject === 'string' ? data.subject : 'بدون موضوع',
    message: typeof data.message === 'string' ? data.message : '',
    category:
      typeof data.category === 'string'
        ? (data.category as ContactRequestCategory)
        : 'general',
    priority:
      typeof data.priority === 'string'
        ? (data.priority as ContactRequestPriority)
        : 'medium',
    status:
      typeof data.status === 'string'
        ? (data.status as SupportTicketStatus)
        : 'new',
    source: typeof data.source === 'string' ? data.source : 'unknown',
    requesterUid:
      typeof data.requesterUid === 'string' ? data.requesterUid : null,
    internalNote:
      typeof data.internalNote === 'string' ? data.internalNote : '',
    createdAt: toDate(data.createdAt),
    updatedAt: toDate(data.updatedAt),
  };
}

export async function submitContactRequest(
  input: SubmitContactRequestInput
): Promise<SubmitContactRequestResult> {
  const submit = httpsCallable<
    SubmitContactRequestInput,
    SubmitContactRequestResult
  >(functions, 'submitContactRequest');

  const result = await submit({
    ...input,
    name: input.name.trim(),
    email: input.email.trim().toLowerCase(),
    phone: input.phone?.trim() || '',
    subject: input.subject.trim(),
    message: input.message.trim(),
  });

  return result.data;
}

export async function subscribeToNewsletter(
  input: SubscribeToNewsletterInput
): Promise<SubscribeToNewsletterResult> {
  const subscribe = httpsCallable<
    SubscribeToNewsletterInput,
    SubscribeToNewsletterResult
  >(functions, 'subscribeToNewsletter');

  const result = await subscribe({
    email: input.email.trim().toLowerCase(),
    source: input.source?.trim() || 'public-site',
  });

  return result.data;
}

export async function listSupportTickets(): Promise<SupportTicket[]> {
  const snapshot = await getDocs(
    query(
      collection(db, SUPPORT_TICKETS_COLLECTION),
      orderBy('createdAt', 'desc'),
      limit(100)
    )
  );

  return snapshot.docs.map(ticket =>
    normalizeTicket(ticket.id, ticket.data())
  );
}

export async function updateSupportTicket(
  input: UpdateSupportTicketInput
): Promise<void> {
  await updateDoc(doc(db, SUPPORT_TICKETS_COLLECTION, input.ticketId), {
    status: input.status,
    internalNote: input.internalNote.trim(),
    updatedAt: Timestamp.now(),
  });
}
